import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not send the reset email. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-5">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto mb-8" />
        {sent ? (
          <div data-testid="forgot-sent" className="bg-card border border-border rounded-2xl p-8">
            <MailCheck className="w-9 h-9 text-brand" />
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100 mt-5">Check your inbox</h1>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              If that email is registered, a reset link is on its way. The link works once and expires in 60 minutes.
            </p>
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary mt-6 hover:underline"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl p-8">
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-slate-100">Forgot your password?</h1>
            <p className="text-sm text-muted-foreground mt-1.5 mb-6">
              Enter the email you signed up with and we will send you a link to set a new password.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Company Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  data-testid="forgot-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  className="mt-1.5"
                />
              </div>
              {error && (
                <div data-testid="forgot-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                data-testid="forgot-submit-button"
                disabled={loading}
                className="w-full h-11 text-sm font-semibold"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link
              to="/login"
              data-testid="back-to-login-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground mt-6 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
